import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { formatInTimeZone } from 'date-fns-tz';
import JobInfoModal from '../../components/dashboard/JobInfoModal';
import ConfirmationModal from '../../components/support/ConfirmationModal'; 
import SuccessModal from '../../components/support/SuccessModal';
import { rates as exchangeRates } from '../../data/exchangeRates';
import './Dashboard.css';
import { useChat } from '../../context/ChatContext';
// --- YARDIMCI BİLEŞENLER VE FONKSİYONLAR ---
const API_URL = process.env.REACT_APP_API_URL;
const getGmtOffsetString = (timezone) => {
    if (!timezone) return '';
    try { return `GMT${formatInTimeZone(new Date(), timezone, 'xxx')}`; } 
    catch (e) { return ''; }
};

// DÜZELTME 1: formatDate fonksiyonu artık timezone parametresini alıyor.
const formatDate = (dateString, timezone) => {
  if (!dateString) return '-';
  // Eğer bir timezone belirtilmemişse, varsayılan olarak 'Europe/Istanbul' kullan
  return formatInTimeZone(new Date(dateString), timezone || 'Europe/Istanbul', 'dd.MM.yyyy HH:mm');
};

const JobStatus = ({ status, t }) => (
  <span className={`status-badge status-${status.toLowerCase()}`}>{t(`job_status_${status.toLowerCase()}`, status)}</span>
);

const FileUploadRow = ({ sourceFile, targetFile, index, onUpload, isUploading, t, onShowDetails, isJobActionDisabled }) => {
    const fileInputRef = useRef(null);
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) { onUpload(sourceFile.id, file); }
    };
    
    let localizedFileName = '';
    if (targetFile) {
        const fileExtension = targetFile.original_filename.split('.').pop();
        localizedFileName = `${t('generic_file_name', { index: index + 1 })}.${fileExtension}`;
    }

    return (
        <div className="file-upload-row">
            <div className="file-info-side">
                <i className="fas fa-file-alt file-icon-big"></i>
                <div className="file-name-group">
                    <strong>{t('source_document', { index: index + 1 })}</strong>
                    <a href={`${API_URL}/files/source/${sourceFile.stored_filename}`} download={sourceFile.original_filename} className="download-source-link"><i className="fas fa-download"></i> {t('download_original')}</a>
                </div>
            </div>
            <div className="file-actions-group">
                <button onClick={onShowDetails} className="details-btn-modern blue"><i className="fas fa-info-circle"></i> {t('details_button')}</button>
                <div className="upload-controls-wrapper">
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} id={`file-input-${sourceFile.id}`} disabled={isJobActionDisabled} />
                    {targetFile ? (
                        <div className="uploaded-info-modern">
                            <span className="uploaded-file-name"><i className="fas fa-check-circle"></i> {localizedFileName}</span>
                            <div className="uploaded-file-actions">
                                <a href={`${API_URL}/completed_files/${targetFile.stored_filename}`} download={targetFile.original_filename} className="action-link icon-only" title={t('download_short')}><i className="fas fa-download"></i></a>
                                {/* DÜZELTME: Sadece 'isJobActionDisabled' kontrolü yapılıyor */}
                                {!isJobActionDisabled && (
                                    <button className="action-link" onClick={() => fileInputRef.current.click()}><i className="fas fa-sync-alt"></i> {t('change')}</button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <button className="upload-btn-modern green" onClick={() => fileInputRef.current.click()} disabled={isUploading || isJobActionDisabled}>
                            {isUploading ? <><i className="fas fa-spinner fa-spin"></i> {t('uploading')}</> : <><i className="fas fa-upload"></i> {t('job_upload_file_btn')}</>}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const JobDetailPage = ({ selectedCurrency }) => {
  const { jobId } = useParams();
  const { t, i18n } = useTranslation();
  const { openChat } = useChat();
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // DÜZELTME: useLocation çağrısı eklendi
  
  const [job, setJob] = useState(null);
  const [files, setFiles] = useState({ source: [], target: [] });
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [uploadingState, setUploadingState] = useState({});
  const [translatorTimezone, setTranslatorTimezone] = useState('Europe/Istanbul'); 
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isDropJobModalOpen, setIsDropJobModalOpen] = useState(false);
const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  
  const fetchJobData = useCallback(async (caller = 'unknown') => {
    if (!token) { setLoading(false); setPageError(t('error_unauthorized')); return; }
    try {
      setLoading(true); setPageError('');
      const [jobResponse, profileResponse, filesResponse] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/jobs/${jobId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/dashboard/profile`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/dashboard/jobs/${jobId}/files`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (!jobResponse.ok) { const errorData = await jobResponse.json().catch(() => ({})); throw new Error(t(errorData.messageKey) || t('error_fetch_job_details')); }
      if (!filesResponse.ok) { const errorData = await filesResponse.json().catch(() => ({})); throw new Error(t(errorData.messageKey) || t('error_fetch_files')); }
      if (!profileResponse.ok) console.warn("Translator profile could not be fetched.");
      
      const jobData = await jobResponse.json();
      const filesData = await filesResponse.json();
      const profileData = profileResponse.ok ? await profileResponse.json() : {};
      
      setJob(jobData);
      setFiles({ source: filesData.filter(f => f.file_type === 'source'), target: filesData.filter(f => f.file_type === 'target') });
     if (profileData.timezone) {
          setTranslatorTimezone(profileData.timezone);
      }
    } catch (err) {
      setPageError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId, token, t]);


  useEffect(() => { fetchJobData('initial load'); }, [fetchJobData]);
  
 const handleFileUpload = async (sourceFileId, fileToUpload) => {
    if (!fileToUpload) return;
    setUploadingState(prev => ({ ...prev, [sourceFileId]: true }));
    
    const formData = new FormData();
    formData.append('translatedFile', fileToUpload);
    // DÜZELTME: Bu satır zaten doğru, sadece varlığını teyit ediyoruz.
    formData.append('sourceFileId', sourceFileId); 

    try {
      const response = await fetch(`${API_URL}/api/dashboard/jobs/${jobId}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      if (!response.ok) { throw new Error(t(data.messageKey) || t('error_file_upload')); }
      alert(t(data.messageKey || 'success_file_uploaded'));
      await fetchJobData('file upload'); // Veriyi yeniden çekerek listeyi güncelle
    } catch (err) {
      alert(err.message);
    } finally {
      setUploadingState(prev => ({ ...prev, [sourceFileId]: false }));
    }
  };

 const handleCompleteJob = async () => {

    if (!allSourceFilesTranslated) {
      alert(t('tooltip_upload_all_files'));
      return;
    }

    if (!window.confirm(t('confirm_complete_job'))) return;
    try {
      const response = await fetch(`${API_URL}/api/dashboard/jobs/${jobId}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) { throw new Error(t(data.messageKey) || t('error_job_complete')); }
     setModalMessage(t(data.messageKey || 'success_job_submitted_for_review'));
      setIsSuccessModalOpen(true);
      setJob(prev => ({ ...prev, status: 'in_review' })); 
    } catch (err) {
      alert(err.message);
    }
  };
  
  const handleDropJob = async () => {
    setIsDropJobModalOpen(false);
    try {
        const response = await fetch(`${API_URL}/api/dashboard/jobs/${jobId}/drop`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
        const data = await response.json();
        if (!response.ok) throw new Error(t(data.messageKey, 'İş bırakılamadı.'));
        alert(t(data.messageKey, 'İş başarıyla bırakıldı.'));
        navigate('/dashboard/jobs');
    } catch (err) {
        alert(err.message);
    }
  };
  const confirmCompleteJob = async () => {
    setIsCompleteModalOpen(false); // Modalı hemen kapat
    try {
      const response = await fetch(`${API_URL}/api/dashboard/jobs/${jobId}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) { throw new Error(t(data.messageKey) || t('error_job_complete')); }
      setModalMessage(t(data.messageKey || 'success_job_submitted_for_review'));
      setIsSuccessModalOpen(true);
      setJob(prev => ({ ...prev, status: 'in_review' })); 
    } catch (err) {
      alert(err.message);
    }
  };
  const canDropJob = () => {
      if (!job || !job.assigned_at) return false;
      if (!['assigned', 'in_progress'].includes(job.status)) return false;
      const assignedTime = new Date(job.assigned_at);
      const currentTime = new Date();
      const differenceInMinutes = (currentTime - assignedTime) / (1000 * 60);
      return differenceInMinutes <= 5;
  };

  const translatorFeeAsNumber = job ? parseFloat(job.translator_fee || 0) : 0;
  const convertedFee = useMemo(() => {
      const rate = exchangeRates[selectedCurrency] || 1;
      return translatorFeeAsNumber / rate;
  }, [translatorFeeAsNumber, selectedCurrency]);



  if (loading) return <div className="dashboard-page page-container"><i className="fas fa-spinner fa-spin"></i> {t('loading_job_details')}</div>;
  if (pageError) return <div className="dashboard-page page-container error-container"><h3>{t('error_occurred')}</h3><p>{pageError}</p><Link to="/dashboard/jobs" className="details-btn">{t('back_to_jobs_list')}</Link></div>;
  if (!job) return <div className="dashboard-page page-container"><p>{t('job_not_found')}</p><Link to="/dashboard/jobs" className="details-btn">{t('back_to_jobs_list')}</Link></div>;

   const confirmDropJob = async () => {
    try {
        const response = await fetch(`${API_URL}/api/dashboard/jobs/${jobId}/drop`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (!response.ok) throw new Error(t(data.messageKey) || 'İş bırakılamadı.');
        alert(t(data.messageKey || 'success_job_dropped'));
        navigate('/dashboard/jobs');
    } catch (err) {
        alert(err.message);
    } finally {
        setIsDropJobModalOpen(false); 
    }
  };
const allSourceFilesTranslated = job && files.source && files.source.length > 0 && files.source.every(sf => files.target.some(tf => tf.source_file_id === sf.id));
  const isJobActionDisabled = ['in_review', 'completed', 'cancelled'].includes(job.status);
  const jobPromptKey = `job_prompt_${job.status.toLowerCase()}`;

  return (

    <>
      <div className="dashboard-page job-detail-page narrow-content">
        <div className="job-detail-header yellow">
          <Link to="/dashboard/jobs" className="back-button"><i className="fas fa-arrow-left"></i></Link>
          <h1>#{job.id} {t('job_detail_title')}</h1>
          {canDropJob() && (<button onClick={() => setIsDropJobModalOpen(true)} className="btn-danger">{t('drop_job_btn')}</button>)}
        </div>
        <div className="detail-grid modern-layout">
          <div className="main-details">
            <div className="info-card-modern blue">
              <h3>{t('project_manager')}</h3>
              <div className="pm-info">
                  <p>{job.manager_first_name || t('support_team')}</p>
                  <button onClick={() => openChat({ jobId: job.id, recipientName: 'Destek Ekibi' })} className="btn-contact-outline">
                      <i className="fas fa-envelope"></i> {t('contact_button')}
                  </button>
              </div>
            </div>
            
            <div className="info-card-modern light-blue">
              <div className="job-status-line">
                <h3>{t('job_current_status')}:</h3>
                <JobStatus status={job.status} t={t} />
              </div>
              <p className="job-prompt">{t(jobPromptKey)}</p>
              
              <div className="file-section">
                <h4>{t('upload_translation')}</h4>
                <div className="file-upload-container">
                    {files.source.map((sourceFile, index) => (
                        <FileUploadRow 
                            key={sourceFile.id}
                            sourceFile={sourceFile}
                            index={index}
                            targetFile={files.target.find(tf => tf.source_file_id === sourceFile.id)}
                            onUpload={handleFileUpload}
                            isUploading={uploadingState[sourceFile.id]}
                            t={t}
                            onShowDetails={() => setIsInfoModalOpen(true)}
							isJobActionDisabled={isJobActionDisabled}
                        />
                    ))}
                </div>
              </div>

              {!isJobActionDisabled && (
             <div className="complete-job-section-footer">
                        <button onClick={() => setIsCompleteModalOpen(true)} className="cta-button complete-job-btn" disabled={!allSourceFilesTranslated} title={!allSourceFilesTranslated ? t('tooltip_upload_all_files') : ''}>
                            <i className="fas fa-flag-checkered"></i> {t('submit_for_review')}
                        </button>
                    </div>
              )}
            </div>
          </div>

          <div className="side-details">
            <div className="side-card-modern">
              <label>{t('deadline').toUpperCase()}</label>
              <p>{formatDate(job.deadline, translatorTimezone)} 
                <span className="gmt-info"> ({getGmtOffsetString(translatorTimezone)})</span></p>
            </div>
            <div className="side-card-modern">
              <label>{t('amount').toUpperCase()}</label>
              <p className="amount">{convertedFee.toLocaleString(i18n.language, { style:'currency', currency: selectedCurrency || 'TRY' })}</p>
            </div>
          </div>
        </div>
      </div>
  
   
      <JobInfoModal 
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        job={job}
        translatorTimezone={translatorTimezone}
        selectedCurrency={selectedCurrency}
      />
	
	  <ConfirmationModal
        isOpen={isDropJobModalOpen}
        onClose={() => setIsDropJobModalOpen(false)}
         onConfirm={handleDropJob}
        title={t('drop_job_btn')}
        message={t('confirm_drop_job')}
        confirmButtonText={t('drop_job_btn')}
        confirmButtonClass="danger"
      />
        <ConfirmationModal
            isOpen={isCompleteModalOpen}
            onClose={() => setIsCompleteModalOpen(false)}
            onConfirm={confirmCompleteJob} // Düzeltilmiş fonksiyonu çağır
            title={t('confirm_submit_title')}
            message={t('confirm_submit_message')}
            confirmButtonText={t('submit_for_review')}
            confirmButtonClass="success" 
        />
	    <SuccessModal
            isOpen={isSuccessModalOpen}
            onClose={() => {
                setIsSuccessModalOpen(false);
                // Eğer iş bırakıldıysa, listeye geri dön
                if (job.status !== 'in_review') {
                    navigate('/dashboard/jobs');
                }
            }}
            title={t('success', 'Başarılı!')}
            message={modalMessage}
        />
    </>
  );
};

export default JobDetailPage;